import { money } from "@/lib/format";

/**
 * PerChildSummary
 *
 * "Per child" sub-totals grid — All-children mode only. Renders one
 * compact card per child showing that child's independent co-parent
 * pairing: total logged, outstanding, linked parents, and net
 * settlement transfers. Hidden when child-scoped (page already shows
 * a single child's view) or when there's only one child with
 * activity (would just duplicate the headline).
 *
 * Extracted from pages/Expenses.jsx in the v77.99.19 Phase 3 frontend
 * decomposition pass. Behaviour and styling preserved verbatim.
 *
 * Props:
 *  - summary:            /expenses/summary API payload (or null)
 *  - currency:           ISO currency code
 *  - userId:             current user id
 *  - selectedChildId:    string | null
 *  - onSelectChild:      (childId) => void   (drills into the child view)
 */
export function PerChildSummary({
  summary,
  currency,
  userId,
  selectedChildId,
  onSelectChild,
}) {
  if (!summary) return null;
  if (selectedChildId) return null;
  if ((summary.by_child || []).length <= 1) return null;

  return (
    <div className="space-y-3" data-testid="by-child-summary">
      <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Per child</div>
      <div className="grid md:grid-cols-2 gap-3">
        {summary.by_child.map((c) => (
          <div
            key={c.child_id}
            className="border border-[#D9E1E3] bg-white rounded-md p-4 hover:bg-[#FAFBFB] transition-colors cursor-pointer"
            onClick={() => onSelectChild?.(c.child_id)}
            role="button"
            tabIndex={0}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") onSelectChild?.(c.child_id);
            }}
            data-testid={`by-child-card-${c.child_id}`}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: c.child_color || "#1F9FA4" }}
                ></span>
                <span className="font-heading text-base font-medium truncate">
                  {c.child_name}
                </span>
              </div>
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                {c.expense_count} {c.expense_count === 1 ? "entry" : "entries"}
              </span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                  Total logged
                </div>
                <div className="font-heading text-lg tabular-nums">
                  {money(c.total, currency)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                  Outstanding
                </div>
                <div
                  className={`font-heading text-lg tabular-nums ${
                    c.outstanding > 0 ? "text-[#C0291D]" : "text-[#116B6E]"
                  }`}
                >
                  {money(c.outstanding, currency)}
                </div>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-[#E4E4E4] text-xs">
              {(c.parents || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {c.parents.map((p) => (
                    <span
                      key={p.id}
                      className="inline-flex items-center px-1.5 py-0.5 border border-[#D9E1E3] bg-[#F7F3FB] rounded-sm text-[10px] text-[#4E3877]"
                      data-testid={`by-child-parent-${c.child_id}-${p.id}`}
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              )}
              {(c.settlements || []).length === 0 ? (
                <div className="text-[#116B6E]">Balances are even.</div>
              ) : (
                <ul className="space-y-0.5">
                  {c.settlements.map((s, i) => {
                    const youOwe = s.from_id === userId;
                    const owedToYou = s.to_id === userId;
                    return (
                      <li
                        key={`${s.from_id}-${s.to_id}-${i}`}
                        className="font-mono-data"
                        data-testid={`by-child-settlement-${c.child_id}-${i}`}
                      >
                        <span className="font-medium">{youOwe ? "You" : s.from_name}</span>{" "}
                        →{" "}
                        <span className="font-medium">{owedToYou ? "you" : s.to_name}</span>{" "}
                        <span className="text-[#C0291D] font-semibold">
                          {money(s.amount, currency)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PerChildSummary;
